import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Plus, ArrowsClockwise, MagnifyingGlass, FileXls, PencilSimple, Trash,
  TrendUp, TrendDown, ArrowUUpLeft, Warning, Scales, FloppyDisk, X
} from '@phosphor-icons/react';

const TXN_TYPES = [
  { value: 'inward', label: 'Stock Inward (Purchase)' },
  { value: 'issue', label: 'Stock Issue (Production)' },
  { value: 'return_in', label: 'Stock Return' },
  { value: 'wastage', label: 'Wastage Entry' },
  { value: 'adjustment_in', label: 'Adjustment In (+)' },
  { value: 'adjustment_out', label: 'Adjustment Out (-)' },
];

const TXN_TYPE_COLORS = {
  inward: 'text-emerald-700 bg-emerald-50', issue: 'text-red-700 bg-red-50',
  return_in: 'text-blue-700 bg-blue-50', wastage: 'text-amber-700 bg-amber-50',
  adjustment_in: 'text-cyan-700 bg-cyan-50', adjustment_out: 'text-purple-700 bg-purple-50',
};

function StockTransactionForm({ materials, onSuccess }) {
  const [form, setForm] = useState({
    material_name: '', txn_type: 'inward', qty: '',
    txn_date: new Date().toISOString().slice(0, 10),
    document_no: '', remarks: '', reference_jo: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.material_name || !form.qty) { toast.error('Material and Qty required'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/inventory/stock/transaction', {
        ...form, qty: parseFloat(form.qty),
      });
      toast.success(`${data.message}. Stock: ${data.new_stock}`);
      setForm({ ...form, qty: '', document_no: '', remarks: '', reference_jo: '' });
      onSuccess?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Card className="rounded-sm shadow-sm">
      <CardHeader className="py-2 px-4 bg-zinc-50 border-b">
        <CardTitle className="text-sm font-bold">New Stock Transaction</CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-bold text-zinc-500 uppercase">Material *</label>
            <select data-testid="stock-material" value={form.material_name} onChange={e => setForm({...form, material_name: e.target.value})} className="h-9 mt-1 text-sm border rounded px-2 w-full bg-white">
              <option value="">Select Material</option>
              {materials.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Type *</label>
            <select data-testid="stock-txn-type" value={form.txn_type} onChange={e => setForm({...form, txn_type: e.target.value})} className="h-9 mt-1 text-sm border rounded px-2 w-full bg-white">
              {TXN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Quantity *</label>
            <Input data-testid="stock-qty" type="number" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})} placeholder="0" className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Transaction Date</label>
            <Input type="date" value={form.txn_date} onChange={e => setForm({...form, txn_date: e.target.value})} className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Document No</label>
            <Input value={form.document_no} onChange={e => setForm({...form, document_no: e.target.value})} placeholder="Invoice/PO/DC No" className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Reference JO</label>
            <Input value={form.reference_jo} onChange={e => setForm({...form, reference_jo: e.target.value})} placeholder="JO Number" className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase">Remarks</label>
            <Input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} placeholder="Optional" className="h-9 mt-1" />
          </div>
        </div>
        <Button data-testid="stock-submit" onClick={submit} disabled={submitting} className="h-9 bg-orange-600 hover:bg-orange-700 text-white font-bold">
          <Plus size={14} className="mr-1" /> {submitting ? 'Processing...' : 'Record Transaction'}
        </Button>
      </CardContent>
    </Card>
  );
}

function StockLedgerTable({ ledger, loading, onEdit, onDelete }) {
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const startEdit = (t) => {
    setEditId(t.txn_id);
    setEditForm({ qty: t.qty, txn_date: t.txn_date || '', document_no: t.document_no || '', remarks: t.remarks || '', reference_jo: t.reference_jo || '' });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/api/inventory/stock/transaction/${editId}`, editForm);
      toast.success('Transaction updated');
      setEditId(null);
      onEdit?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Update failed'); }
  };

  const handleDelete = async (txnId) => {
    if (!window.confirm('Delete this transaction? Stock will be reversed.')) return;
    try {
      await api.delete(`/api/inventory/stock/transaction/${txnId}`);
      toast.success('Transaction deleted');
      onDelete?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <p className="p-4 text-zinc-400">Loading...</p>;
  if (!ledger.length) return <p className="p-4 text-zinc-400">No transactions found.</p>;

  return (
    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-zinc-50 z-10">
          <tr className="border-b">
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Date</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Doc No</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Material</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Type</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Qty</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Before</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">After</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Ref JO</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Remarks</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">By</th>
            <th className="p-1.5 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {ledger.map((t) => {
            const isEditing = editId === t.txn_id;
            return (
              <tr key={t.txn_id || `${t.material_name}-${t.created_at}`} className="border-b hover:bg-zinc-50">
                <td className="p-1.5">
                  {isEditing ? <Input type="date" value={editForm.txn_date} onChange={e => setEditForm({...editForm, txn_date: e.target.value})} className="h-6 text-xs w-28" /> : (t.txn_date || t.created_at?.slice(0, 10) || '--')}
                </td>
                <td className="p-1.5">
                  {isEditing ? <Input value={editForm.document_no} onChange={e => setEditForm({...editForm, document_no: e.target.value})} className="h-6 text-xs w-24" /> : (t.document_no || '--')}
                </td>
                <td className="p-1.5 font-bold text-zinc-800">{t.material_name}</td>
                <td className="p-1.5 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${TXN_TYPE_COLORS[t.txn_type] || ''}`}>
                    {t.txn_type?.replace('_', ' ')}
                  </span>
                </td>
                <td className="p-1.5 text-center font-bold">
                  {isEditing ? <Input type="number" value={editForm.qty} onChange={e => setEditForm({...editForm, qty: parseFloat(e.target.value) || 0})} className="h-6 text-xs w-16" /> : t.qty}
                </td>
                <td className="p-1.5 text-center text-zinc-500">{t.stock_before}</td>
                <td className="p-1.5 text-center font-bold">{t.stock_after}</td>
                <td className="p-1.5">
                  {isEditing ? <Input value={editForm.reference_jo} onChange={e => setEditForm({...editForm, reference_jo: e.target.value})} className="h-6 text-xs w-20" /> : (t.reference_jo || '--')}
                </td>
                <td className="p-1.5 text-zinc-500">
                  {isEditing ? <Input value={editForm.remarks} onChange={e => setEditForm({...editForm, remarks: e.target.value})} className="h-6 text-xs w-24" /> : (t.remarks || '--')}
                </td>
                <td className="p-1.5">{t.created_by || '--'}</td>
                <td className="p-1.5">
                  {isEditing ? (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={saveEdit} className="h-6 w-6 p-0 bg-emerald-600 text-white"><FloppyDisk size={10} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-6 w-6 p-0"><X size={10} /></Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(t)} className="h-6 w-6 p-0 text-blue-600"><PencilSimple size={10} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(t.txn_id)} className="h-6 w-6 p-0 text-red-500"><Trash size={10} /></Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StockMovementReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data: res } = await api.get('/api/inventory/reports/stock-movement');
      setData(res);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const exportCSV = () => {
    const headers = ['Material', 'Code', 'Category', 'UQC', 'Opening', 'Inward', 'Issued', 'Returned', 'Wastage', 'Adj In', 'Adj Out', 'Closing', 'Reserved', 'Available'];
    const rows = data.map(r => [r.material_name, r.material_code, r.category, r.uqc, r.opening_stock, r.inward, r.issued, r.returned, r.wastage, r.adjustment_in, r.adjustment_out, r.closing_stock, r.reserved_stock, r.available_stock]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_movement_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p className="p-4 text-zinc-400">Loading...</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <Button onClick={fetch} size="sm" className="h-8 bg-blue-600 text-white"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <Button onClick={exportCSV} size="sm" variant="outline" className="h-8"><FileXls size={14} className="mr-1" /> Export</Button>
        <span className="text-xs text-zinc-500">{data.length} materials</span>
      </div>
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-50 z-10">
            <tr className="border-b">
              <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Material</th>
              <th className="p-1.5 font-bold uppercase text-zinc-500">Cat</th>
              <th className="p-1.5 font-bold uppercase text-zinc-500">UQC</th>
              <th className="p-1.5 font-bold uppercase text-zinc-500 bg-zinc-100">Opening</th>
              <th className="p-1.5 font-bold uppercase text-emerald-600">Inward</th>
              <th className="p-1.5 font-bold uppercase text-red-600">Issued</th>
              <th className="p-1.5 font-bold uppercase text-blue-600">Return</th>
              <th className="p-1.5 font-bold uppercase text-amber-600">Wastage</th>
              <th className="p-1.5 font-bold uppercase text-cyan-600">Adj In</th>
              <th className="p-1.5 font-bold uppercase text-purple-600">Adj Out</th>
              <th className="p-1.5 font-bold uppercase text-zinc-800 bg-zinc-100">Closing</th>
              <th className="p-1.5 font-bold uppercase text-orange-600">Reserved</th>
              <th className="p-1.5 font-bold uppercase text-emerald-700 bg-emerald-50">Available</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.material_name} className="border-b hover:bg-zinc-50">
                <td className="p-1.5 font-bold text-zinc-800">{r.material_name}</td>
                <td className="p-1.5 text-center text-zinc-500">{r.category}</td>
                <td className="p-1.5 text-center font-bold">{r.uqc}</td>
                <td className="p-1.5 text-center bg-zinc-50">{r.opening_stock}</td>
                <td className="p-1.5 text-center text-emerald-700 font-bold">{r.inward || '--'}</td>
                <td className="p-1.5 text-center text-red-600 font-bold">{r.issued || '--'}</td>
                <td className="p-1.5 text-center text-blue-600">{r.returned || '--'}</td>
                <td className="p-1.5 text-center text-amber-600">{r.wastage || '--'}</td>
                <td className="p-1.5 text-center text-cyan-600">{r.adjustment_in || '--'}</td>
                <td className="p-1.5 text-center text-purple-600">{r.adjustment_out || '--'}</td>
                <td className="p-1.5 text-center font-bold bg-zinc-50">{r.closing_stock}</td>
                <td className="p-1.5 text-center text-orange-600">{r.reserved_stock}</td>
                <td className={`p-1.5 text-center font-bold bg-emerald-50 ${r.available_stock < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{r.available_stock}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={13} className="p-6 text-center text-zinc-400">No materials</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockSummaryTable({ summary, loading }) {
  if (loading) return <p className="p-4 text-zinc-400">Loading...</p>;
  if (!summary.length) return <p className="p-4 text-zinc-400">No materials.</p>;
  const getRowBg = (m) => {
    if (m.below_min) return 'bg-red-50';
    if (m.below_reorder) return 'bg-amber-50';
    return '';
  };
  const renderStatusBadge = (m) => {
    if (m.below_min) return <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">Below Min</span>;
    if (m.below_reorder) return <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">Reorder</span>;
    return <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-700">OK</span>;
  };
  return (
    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-zinc-50 z-10">
          <tr className="border-b">
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Material</th>
            <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Category</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">UQC</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Opening</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Current</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Reserved</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Available</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Min</th>
            <th className="p-1.5 font-bold uppercase text-zinc-500">Status</th>
          </tr>
        </thead>
        <tbody>
          {summary.map(m => (
            <tr key={m.material_name} className={`border-b hover:bg-zinc-50 ${getRowBg(m)}`}>
              <td className="p-1.5 font-bold text-zinc-800">{m.material_name}</td>
              <td className="p-1.5 text-zinc-600">{m.category}</td>
              <td className="p-1.5 text-center">{m.uqc}</td>
              <td className="p-1.5 text-center">{m.opening_stock}</td>
              <td className="p-1.5 text-center font-bold">{m.current_stock}</td>
              <td className="p-1.5 text-center text-amber-700">{m.reserved_stock}</td>
              <td className={`p-1.5 text-center font-bold ${m.below_min ? 'text-red-600' : 'text-emerald-600'}`}>{m.available_stock}</td>
              <td className="p-1.5 text-center">{m.min_stock}</td>
              <td className="p-1.5 text-center">{renderStatusBadge(m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StockOperations() {
  const [activeTab, setActiveTab] = useState('transaction');
  const [materials, setMaterials] = useState([]);
  const [summary, setSummary] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerFilter, setLedgerFilter] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [matRes, summRes, ledRes] = await Promise.all([
        api.get('/api/inventory/masters/materials'),
        api.get('/api/inventory/stock/summary'),
        api.get('/api/inventory/stock/ledger?limit=200'),
      ]);
      setMaterials(matRes.data.map(m => m.material_name));
      setSummary(summRes.data);
      setLedger(ledRes.data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchFilteredLedger = async () => {
    try {
      const params = ledgerFilter ? `material_name=${encodeURIComponent(ledgerFilter)}&limit=500` : 'limit=200';
      const { data } = await api.get(`/api/inventory/stock/ledger?${params}`);
      setLedger(data);
    } catch { toast.error('Failed'); }
  };

  const tabs = [
    { key: 'transaction', label: 'New Transaction' },
    { key: 'ledger', label: 'Stock Ledger' },
    { key: 'summary', label: 'Stock Summary' },
    { key: 'movement', label: 'Stock Movement' },
  ];

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Stock Operations</h1>
            <p className="text-sm text-zinc-500 mt-2">Manage stock inward, issue, returns, wastage, and adjustments</p>
          </div>
          <Button onClick={fetchAll} variant="outline" className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Card className="rounded-sm shadow-sm">
            <CardContent className="p-3 text-center">
              <p className="text-xs font-bold uppercase text-zinc-500">Total Materials</p>
              <p className="text-2xl font-light">{materials.length}</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm shadow-sm border-red-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs font-bold uppercase text-red-500">Below Min Stock</p>
              <p className="text-2xl font-light text-red-600">{summary.filter(s => s.below_min).length}</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm shadow-sm border-amber-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs font-bold uppercase text-amber-500">Below Reorder</p>
              <p className="text-2xl font-light text-amber-600">{summary.filter(s => s.below_reorder).length}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-1 border-b border-zinc-200">
          {tabs.map(tab => (
            <button key={tab.key} data-testid={`stock-tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-bold transition-colors ${activeTab === tab.key ? 'border-b-2 border-orange-600 text-orange-700' : 'text-zinc-500 hover:text-zinc-800'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'transaction' && <StockTransactionForm materials={materials} onSuccess={fetchAll} />}

        {activeTab === 'summary' && (
          <Card className="rounded-sm shadow-sm"><CardContent className="p-0"><StockSummaryTable summary={summary} loading={loading} /></CardContent></Card>
        )}

        {activeTab === 'ledger' && (
          <div className="space-y-3">
            <Card className="rounded-sm shadow-sm">
              <CardContent className="p-3 flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs font-bold text-zinc-500 uppercase">Filter by Material</label>
                  <select data-testid="ledger-filter" value={ledgerFilter} onChange={e => setLedgerFilter(e.target.value)} className="h-9 mt-1 text-sm border rounded px-2 w-full bg-white">
                    <option value="">All Materials</option>
                    {materials.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <Button onClick={fetchFilteredLedger} className="h-9 bg-blue-600 text-white"><MagnifyingGlass size={14} className="mr-1" /> Filter</Button>
              </CardContent>
            </Card>
            <Card className="rounded-sm shadow-sm">
              <CardContent className="p-0">
                <StockLedgerTable ledger={ledger} loading={loading} onEdit={fetchAll} onDelete={fetchAll} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'movement' && <StockMovementReport />}
      </div>
    </Layout>
  );
}
